import React, { useState, useEffect } from 'react';
import axiosInstance from '../lib/axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Key, Sparkles, Loader2, CheckCircle2, Plus, Trash2, Edit2, X, ArrowUp, ArrowDown } from 'lucide-react';
import PromptImprover from '../tiptap/components/PromptImprover';
// Modelos/Prompts/Legal se renderizan como secciones propias en AdminPage.

/**
 * T-037 — administrator settings shared by the root and tiptap AdminPanels.
 *
 * Store-agnostic on purpose (no config/auth store): everything it needs comes
 * from the admin API (`/admin/*`) and the models list (`/ai/models`). The base
 * OpenRouter key is WRITE-ONLY — the backend only ever returns a mask.
 */
const emptyForm = { id: null, title: '', category: 'Texto', steps: [{ instruction: '', model: '', advisors: [] }] };

const AdminSettings = ({ section = null } = {}) => {
  // section: 'key' | 'commands' | null. null = ambos (compat). El /admin usa el
  // menú lateral; los cards de modelos/prompts/legal los renderiza AdminPage.
  const show = (s) => !section || section === s;
  const [settings, setSettings] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [savingKey, setSavingKey] = useState(false);
  const [testingKey, setTestingKey] = useState(false);

  const [commands, setCommands] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    loadSettings();
    loadCommands();
  }, []);

  const loadSettings = async () => {
    try {
      const res = await axiosInstance.get('/admin/settings');
      setSettings(res.data);
    } catch (e) {
      if (e.response?.status === 403) toast.error('Solo administradores');
    }
  };

  const loadCommands = async () => {
    try {
      const res = await axiosInstance.get('/admin/commands');
      setCommands(res.data || []);
    } catch (e) {
      setCommands([]);
    }
  };

  // --- Base key ---
  const saveKey = async () => {
    setSavingKey(true);
    try {
      await axiosInstance.put('/admin/settings', { openrouter_base_key: newKey });
      toast.success(newKey ? 'Llave base guardada' : 'Llave base eliminada');
      setNewKey('');
      loadSettings();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar la llave');
    } finally {
      setSavingKey(false);
    }
  };

  const testKey = async () => {
    setTestingKey(true);
    try {
      // Test the typed candidate if present, else the effective saved key.
      const body = newKey ? { api_key: newKey } : {};
      const res = await axiosInstance.post('/admin/test-key', body);
      toast.success(res.data?.detail || 'Llave válida');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'La llave no es válida');
    } finally {
      setTestingKey(false);
    }
  };

  // --- System commands ---
  const startNew = () => { setForm(emptyForm); setEditing(true); };
  const startEdit = (cmd) => {
    setForm({
      id: cmd.id,
      title: cmd.title || '',
      category: cmd.category || 'Texto',
      steps: (cmd.steps && cmd.steps.length ? cmd.steps : [{ instruction: cmd.instruction || '' }])
        .map((s) => ({ instruction: s.instruction || '', model: s.model || '', advisors: Array.isArray(s.advisors) ? s.advisors.slice(0, 3) : [] })),
    });
    setEditing(true);
  };
  const cancelEdit = () => { setForm(emptyForm); setEditing(false); };

  const setStep = (i, v) => setForm((p) => ({ ...p, steps: p.steps.map((s, idx) => idx === i ? { ...s, instruction: v } : s) }));
  const addStep = () => setForm((p) => ({ ...p, steps: [...p.steps, { instruction: '', model: '', advisors: [] }] }));
  const removeStep = (i) => setForm((p) => p.steps.length <= 1 ? p : ({ ...p, steps: p.steps.filter((_, idx) => idx !== i) }));
  const moveStep = (i, d) => setForm((p) => {
    const t = i + d;
    if (t < 0 || t >= p.steps.length) return p;
    const steps = [...p.steps];
    [steps[i], steps[t]] = [steps[t], steps[i]];
    return { ...p, steps };
  });

  const saveCommand = async () => {
    const steps = form.steps.map((s) => ({ instruction: (s.instruction || '').trim(), model: s.model || '', advisors: Array.isArray(s.advisors) ? s.advisors.slice(0, 3) : [] })).filter((s) => s.instruction);
    if (!form.title.trim() || steps.length === 0) {
      toast.error('Título y al menos un paso son requeridos');
      return;
    }
    const payload = { title: form.title.trim(), category: form.category, steps };
    try {
      if (form.id) {
        await axiosInstance.put(`/admin/commands/${form.id}`, payload);
        toast.success('Comando base actualizado');
      } else {
        await axiosInstance.post('/admin/commands', payload);
        toast.success('Comando base creado');
      }
      cancelEdit();
      loadCommands();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar el comando');
    }
  };

  const deleteCommand = async (id) => {
    try {
      await axiosInstance.delete(`/admin/commands/${id}`);
      toast.success('Comando eliminado');
      loadCommands();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al eliminar');
    }
  };

  return (
    <div className="space-y-6">
      {show('key') && (
      /* (a) Base key */
      <div className="glass rounded-xl p-6 space-y-3">
        <label className="text-sm font-medium flex items-center gap-2">
          <Key className="w-4 h-4 text-purple-600" />
          Llave base de OpenRouter (plataforma)
        </label>
        <div className="text-sm text-slate-600 dark:text-slate-300">
          Estado:{' '}
          {settings?.base_key_set ? (
            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800">
              Configurada · {settings.base_key_masked}
            </Badge>
          ) : (
            <Badge variant="outline" className="bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:border-slate-700">
              No configurada
            </Badge>
          )}
        </div>
        <Input
          data-testid="admin-base-key-input"
          type="password"
          placeholder="sk-or-v1-… (deja vacío y guarda para eliminarla)"
          value={newKey}
          onChange={(e) => setNewKey(e.target.value)}
          className="font-mono"
        />
        <p className="text-xs text-slate-500 dark:text-slate-400">
          La llave nunca se muestra completa; solo se envía al guardar (write-only). Usada por los usuarios sin su propia llave BYOK.
        </p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="admin-save-base-key" onClick={saveKey} disabled={savingKey}
            className="bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700">
            {savingKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Guardar llave
          </Button>
          <Button data-testid="admin-test-base-key" variant="secondary" onClick={testKey} disabled={testingKey}>
            {testingKey ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
            Probar
          </Button>
        </div>
      </div>
      )}

      {show('commands') && (
      /* System commands */
      <div className="glass rounded-xl p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <label className="text-sm font-medium flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-600" />
            Comandos Rápidos de IA
          </label>
          {!editing && (
            <Button data-testid="admin-new-command" size="sm" onClick={startNew}>
              <Plus className="w-4 h-4 mr-1" /> Nuevo
            </Button>
          )}
        </div>

        {editing && (
          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-3">
            <Input
              data-testid="admin-command-title"
              placeholder="Título del comando"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
            <select
              data-testid="admin-command-category"
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value })}
              className="w-full h-10 rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 px-3 text-sm"
            >
              <option value="Texto" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">Texto</option>
              <option value="Imagen" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">Imagen</option>
              <option value="Descripción" className="bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100">Descripción</option>
            </select>
            {form.steps.map((step, i) => (
              <div key={i} className="rounded-md border border-slate-200 dark:border-slate-700 p-2 space-y-1">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-slate-600 dark:text-slate-300">Paso {i + 1}</span>
                  <div className="flex gap-1 items-center">
                    {/* Mejorar con IA por paso (pedido del dueño): mismo componente
                        que usa el gestor de comandos del editor (CommandManagement). */}
                    <PromptImprover
                      currentPrompt={step.instruction}
                      onImproved={(improved) => setStep(i, improved)}
                    />
                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveStep(i, -1)} disabled={i === 0}><ArrowUp className="w-4 h-4" /></Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => moveStep(i, 1)} disabled={i === form.steps.length - 1}><ArrowDown className="w-4 h-4" /></Button>
                    <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeStep(i)} disabled={form.steps.length <= 1}><X className="w-4 h-4" /></Button>
                  </div>
                </div>
                <Textarea
                  data-testid={`admin-command-step-${i}`}
                  placeholder="Instrucción para la IA…"
                  value={step.instruction}
                  onChange={(e) => setStep(i, e.target.value)}
                  rows={3}
                  className="resize-none"
                />
              </div>
            ))}
            <Button type="button" variant="outline" size="sm" onClick={addStep} className="w-full">
              <Plus className="w-4 h-4 mr-1" /> Agregar paso
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={cancelEdit} className="flex-1">Cancelar</Button>
              <Button data-testid="admin-save-command" onClick={saveCommand} className="flex-1 bg-gradient-to-r from-sky-500 to-blue-600 hover:from-sky-600 hover:to-blue-700">
                {form.id ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {commands.length === 0 && !editing && (
            <p className="text-sm text-slate-500 dark:text-slate-400">No hay comandos rápidos de IA.</p>
          )}
          {commands.map((cmd) => (
            <div key={cmd.id} data-testid={`admin-command-${cmd.id}`} className="flex items-center justify-between flex-wrap gap-2 rounded-md border border-slate-200 dark:border-slate-700 p-3">
              <div>
                <div className="font-medium text-sm">{cmd.title}</div>
                <div className="flex gap-1 mt-1">
                  <Badge variant="outline">{cmd.category || 'Texto'}</Badge>
                  {Array.isArray(cmd.steps) && cmd.steps.length >= 2 && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800">
                      Macro · {cmd.steps.length} pasos
                    </Badge>
                  )}
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => startEdit(cmd)} data-testid={`admin-edit-command-${cmd.id}`}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-900/50" onClick={() => deleteCommand(cmd.id)} data-testid={`admin-delete-command-${cmd.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>
      )}
    </div>
  );
};

export default AdminSettings;
